// src/models/login.ts

// ==================== YOUR EXISTING INTERFACES ====================

export interface LoginResponse {
    message: string;
    mfaType: string;
    pwdNeedsChange: boolean;
    status: string;
    token: string;
}

export interface LoginPayload {
    mobile: string;
    // password: string;
    deviceId: string;
    applicationId: string;
}

export interface VerifyMfaPayload {
    otp: string;
    deviceId: string;
    mfaType: string;
    deviceInfo: string;
    appVersion: string;
    os: string;
}

export interface VerifyMfaResponse {
    status: string;
    data: LoginInfo;
    refreshToken: string;
    ssoToken: string;
    refresh_token?: string;
    redirect_url?: string;
    message?: string;
}

export interface VerifyMfaSSOResponse {
    email: string;
    kra_status: string;
    message: string;
    mobile: string;
    redirectUrl: string;
    status: string;
    token: string;
}

export interface RedirectPayload {
    ssoToken: string;
    applicationId: string | null; // `searchParams.get('applicationId')` might return null
}

export interface LoginInfo {
    access_token: string;
    account_age_nudge: boolean;
    admin_reject_nudge: string;
    choice_of_nominee: string;
    deviceId: string;
    dp_client_id: string;
    dp_id: string;
    email: string;
    exchanges: string[];
    isDematActive: boolean;
    is_maintainance: boolean;
    is_promoter_client: boolean;
    kra_status: string;
    login_time: string;
    mfTradeActive: boolean;
    mfaType: string;
    mfa_type: string;
    mobile: string;
    oc_token: string;
    others: { userCode: string; POA: number };
    product_types: string[];
    re_kyc_type: string;
    referral_id: string;
    ssoToken: string;
    user_id: string;
    user_name: string;
    user_preference: string;
    mpin_enabled?: boolean;
    message?: string;
    tradingActive?: boolean;
    redirectUrl?: string;
    is_mpin_set: boolean;
    // Additional field for internal use
    auth_token?: string;
    identifier: string;
}

export interface RefreshData {
    deviceId: string;
    mfaType: string;
    ssoToken: string;
    tradingActive?: boolean;
    refreshToken?: string;
}

// ==================== API RESPONSE MAPPING INTERFACES ====================

/**
 * Raw API response interface that handles field variations
 * This represents what actually comes from different API endpoints
 */
export interface ApiUserResponse {
    access_token: string;
    user_name?: string;
    name?: string; // Alternative field name
    login_time?: string;
    email: string;
    mobile?: string;
    ph?: string; // Alternative field name for mobile
    exchanges?: string[];
    product_types?: string[];
    others?: { userCode: string; POA: number };
    user_id?: string;
    id?: string; // Alternative field name for user_id
    referral_id?: string;
    tradingActive?: boolean;
    isDematActive?: boolean;
    mfTradeActive?: boolean;
    dp_id?: string;
    dp_client_id?: string;
    mfa_type?: string;
    mfaType?: string;
    oc_token?: string;
    kra_status?: string;
    re_kyc_type?: string;
    choice_of_nominee?: string;
    account_age_nudge?: boolean;
    is_promoter_client?: boolean;
    user_preference?: string;
    admin_reject_nudge?: string;
    is_mpin_set?: boolean;
    mpin_enabled?: boolean;
    is_maintainance?: boolean;
    sso_token?: string; // Alternative field name for ssoToken
    client_code?: string; // Additional field that might come from API
    deviceId?: string;
    message?: string;
    redirectUrl?: string;
    redirect_url?: string; // Alternative field name
    identifier?: string;
}

/**
 * SSO Login Request interface
 */
export interface SsoLoginRequest {
    sso_auth_token: string;
    application_id: string;
}

/**
 * SSO Login API Response interface
 */
export interface SsoLoginApiResponse {
    status: 'success' | 'error' | 'failure';
    data?: ApiUserResponse;
    message?: string;
}

/**
 * Login event payload for analytics
 */
export interface LoginEventPayload {
    email: string | null;
    mobile: string | null;
}

/**
 * OTP data payload interface
 */
export interface OtpDataPayload {
    data: any;
    user_id: string | null;
}

/**
 * Email and mobile payload interface
 */
export interface EmailMobilePayload {
    email: string | null;
    mobile: string | null;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Convert API response to LoginInfo format
 * Handles all field name variations and ensures consistency
 */
export function mapApiResponseToLoginInfo(
    apiData: ApiUserResponse,
    ssoAuthToken?: string,
    deviceId?: string
): LoginInfo {
    return {
        access_token: apiData.access_token || '',
        account_age_nudge: apiData.account_age_nudge || false,
        admin_reject_nudge: apiData.admin_reject_nudge || '',
        choice_of_nominee: apiData.choice_of_nominee || '',
        deviceId: apiData.deviceId || deviceId || '',
        dp_client_id: apiData.dp_client_id || '',
        dp_id: apiData.dp_id || '',
        email: apiData.email || '',
        exchanges: apiData.exchanges || [],
        isDematActive: apiData.isDematActive || false,
        is_maintainance: apiData.is_maintainance || false,
        is_promoter_client: apiData.is_promoter_client || false,
        kra_status: apiData.kra_status || '',
        login_time: apiData.login_time || new Date().toISOString(),
        mfTradeActive: apiData.mfTradeActive || false,
        mfaType: apiData.mfaType || apiData.mfa_type || '',
        mfa_type: apiData.mfa_type || apiData.mfaType || '',
        mobile: apiData.mobile || apiData.ph || '',
        oc_token: apiData.oc_token || '',
        others: apiData.others || { userCode: '', POA: 0 },
        product_types: apiData.product_types || [],
        re_kyc_type: apiData.re_kyc_type || '',
        referral_id: apiData.referral_id || '',
        ssoToken: apiData.sso_token || ssoAuthToken || '',
        user_id: apiData.user_id || apiData.id || '',
        user_name: apiData.user_name || apiData.name || '',
        user_preference: apiData.user_preference || 'INVESTOR',
        mpin_enabled: apiData.mpin_enabled || apiData.is_mpin_set || false,
        message: apiData.message,
        tradingActive: apiData.tradingActive,
        redirectUrl: apiData.redirectUrl || apiData.redirect_url,
        is_mpin_set: apiData.is_mpin_set || false,
        auth_token: apiData.access_token || '', // Add for internal consistency
    };
}

/**
 * Convert VerifyMfaResponse to LoginInfo format
 */
export function mapMfaResponseToLoginInfo(
    mfaResponse: VerifyMfaResponse,
    deviceId?: string
): LoginInfo {
    if (mfaResponse.data) {
        // If data is already in LoginInfo format
        return {
            ...mfaResponse.data,
            ssoToken: mfaResponse.ssoToken || mfaResponse.data.ssoToken,
            auth_token: mfaResponse.data.access_token,
            deviceId: mfaResponse.data.deviceId || deviceId || '',
            redirectUrl: mfaResponse.redirect_url || mfaResponse.data.redirectUrl,
            message: mfaResponse.message || mfaResponse.data.message,
        };
    } else {
        // Handle case where data might be in different format
        throw new Error('Invalid MFA response format');
    }
}

/**
 * Convert LoginInfo to RefreshData format
 */
export function mapLoginInfoToRefreshData(
    loginInfo: LoginInfo,
    refreshToken?: string
): RefreshData {
    return {
        deviceId: loginInfo.deviceId,
        mfaType: loginInfo.mfaType || loginInfo.mfa_type,
        ssoToken: loginInfo.ssoToken,
        tradingActive: loginInfo.tradingActive,
        refreshToken: refreshToken,
    };
}

// ==================== TYPE GUARDS ====================

/**
 * Type guard to validate API response
 */
export function isValidApiUserResponse(data: any): data is ApiUserResponse {
    return (
        data &&
        typeof data === 'object' &&
        typeof data.access_token === 'string' &&
        typeof data.email === 'string' &&
        (typeof data.user_id === 'string' || typeof data.id === 'string')
    );
}

/**
 * Type guard to validate LoginInfo
 */
export function isValidLoginInfo(data: any): data is LoginInfo {
    return (
        data &&
        typeof data === 'object' &&
        typeof data.access_token === 'string' &&
        typeof data.user_id === 'string' &&
        typeof data.email === 'string' &&
        typeof data.user_name === 'string'
    );
}

/**
 * Type guard to validate VerifyMfaResponse
 */
export function isValidMfaResponse(data: any): data is VerifyMfaResponse {
    return (
        data &&
        typeof data === 'object' &&
        typeof data.status === 'string' &&
        data.data &&
        typeof data.ssoToken === 'string'
    );
}

/**
 * Type guard to validate LoginResponse
 */
export function isValidLoginResponse(data: any): data is LoginResponse {
    return (
        data &&
        typeof data === 'object' &&
        typeof data.status === 'string' &&
        typeof data.token === 'string' &&
        typeof data.mfaType === 'string'
    );
}

/**
 * Type guard to validate RefreshData
 */
export function isValidRefreshData(data: any): data is RefreshData {
    return (
        data &&
        typeof data === 'object' &&
        typeof data.deviceId === 'string' &&
        typeof data.mfaType === 'string' &&
        typeof data.ssoToken === 'string'
    );
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Normalize user data from any source to LoginInfo format
 */
export function normalizeUserData(
    data: ApiUserResponse | LoginInfo | any,
    ssoToken?: string,
    deviceId?: string
): LoginInfo {
    // If data is already LoginInfo format
    if (isValidLoginInfo(data)) {
        return {
            ...data,
            auth_token: data.auth_token || data.access_token,
            ssoToken: data.ssoToken || ssoToken || '',
        };
    }

    // If data is API response format
    if (isValidApiUserResponse(data)) {
        return mapApiResponseToLoginInfo(data, ssoToken, deviceId);
    }

    // Try to map whatever we have
    return mapApiResponseToLoginInfo(data, ssoToken, deviceId);
}

/**
 * Extract client code from user data (handles different field names)
 */
export function getClientCode(userData: LoginInfo | ApiUserResponse): string {
    if ('user_id' in userData && userData.user_id) {
        return userData.user_id;
    }
    if ('id' in userData && userData.id) {
        return userData.id;
    }
    if ('client_code' in userData && userData.client_code) {
        return userData.client_code;
    }
    return '';
}

/**
 * Get mobile number from user data (handles different field names)
 */
export function getMobileNumber(userData: LoginInfo | ApiUserResponse): string {
    if ('mobile' in userData && userData.mobile) {
        return userData.mobile;
    }
    if ('ph' in userData && userData.ph) {
        return userData.ph;
    }
    return '';
}

/**
 * Get user name from user data (handles different field names)
 */
export function getUserName(userData: LoginInfo | ApiUserResponse): string {
    if ('user_name' in userData && userData.user_name) {
        return userData.user_name;
    }
    if ('name' in userData && userData.name) {
        return userData.name;
    }
    return '';
}

/**
 * Check if user has specific permissions
 */
export function hasUserPermission(userData: LoginInfo, permission: string): boolean {
    switch (permission.toLowerCase()) {
        case 'trading':
            return userData.tradingActive || false;
        case 'demat':
            return userData.isDematActive || false;
        case 'mf':
        case 'mutualfund':
            return userData.mfTradeActive || false;
        case 'mpin':
            return userData.mpin_enabled || userData.is_mpin_set || false;
        default:
            return false;
    }
}

/**
 * Validate required user fields
 */
export function validateUserData(userData: LoginInfo): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!userData.access_token) {
        errors.push('Access token is required');
    }

    // if (!userData.user_id) {
    //     errors.push('User ID is required');
    // }

    if (!userData.email) {
        errors.push('Email is required');
    }

    if (!userData.mobile) {
        errors.push('Mobile number is required');
    }

    if (!userData.user_name) {
        errors.push('User name is required');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Clean and format mobile number
 */
export function formatMobileNumber(mobile: string): string {
    // Remove all non-digit characters
    const cleaned = mobile.replace(/\D/g, '');

    // If it starts with +91 or 91, remove country code
    if (cleaned.startsWith('91') && cleaned.length === 12) {
        return cleaned.substring(2);
    }

    return cleaned;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validate mobile number format (Indian)
 */
export function isValidMobileNumber(mobile: string): boolean {
    const cleaned = formatMobileNumber(mobile);
    return /^[6-9]\d{9}$/.test(cleaned);
}

/**
 * Get user display name (handles different name formats)
 */
export function getUserDisplayName(userData: LoginInfo): string {
    if (userData.user_name) {
        // Convert to title case
        return userData.user_name
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
    return 'User';
}

/**
 * Check if user account needs attention
 */
export function getUserAccountStatus(userData: LoginInfo): {
    status: 'active' | 'attention_needed' | 'inactive';
    issues: string[];
    suggestions: string[];
} {
    const issues: string[] = [];
    const suggestions: string[] = [];

    if (!userData.tradingActive) {
        issues.push('Trading is not active');
        suggestions.push('Contact support to activate trading');
    }

    if (!userData.isDematActive) {
        issues.push('Demat account is not active');
        suggestions.push('Complete KYC to activate demat account');
    }

    if (!userData.mpin_enabled && !userData.is_mpin_set) {
        issues.push('MPIN is not set');
        suggestions.push('Set up MPIN for secure transactions');
    }

    if (userData.kra_status !== 'APPROVED') {
        issues.push(`KRA status: ${userData.kra_status}`);
        suggestions.push('Complete KYC verification');
    }

    if (userData.account_age_nudge) {
        issues.push('Account needs verification');
        suggestions.push('Complete account verification process');
    }

    let status: 'active' | 'attention_needed' | 'inactive' = 'active';

    if (issues.length > 2) {
        status = 'inactive';
    } else if (issues.length > 0) {
        status = 'attention_needed';
    }

    return { status, issues, suggestions };
}

/**
 * Get available trading instruments for user
 */
export function getAvailableInstruments(userData: LoginInfo): {
    equity: boolean;
    derivatives: boolean;
    commodity: boolean;
    currency: boolean;
    mutualFunds: boolean;
} {
    const exchanges = userData.exchanges || [];
    const productTypes = userData.product_types || [];

    return {
        equity: exchanges.includes('NSE') || exchanges.includes('BSE'),
        derivatives: exchanges.includes('NFO') || exchanges.includes('BFO'),
        commodity: exchanges.includes('MCX') || exchanges.includes('NCDEX'),
        currency: exchanges.includes('CDS'),
        mutualFunds: userData.mfTradeActive || false,
    };
}

// ==================== CONSTANTS ====================

/**
 * User preference types
 */
export const USER_PREFERENCES = {
    INVESTOR: 'INVESTOR',
    TRADER: 'TRADER',
    BOTH: 'BOTH'
} as const;

/**
 * MFA types
 */
export const MFA_TYPES = {
    TOTP: 'totp',
    SMS: 'sms',
    EMAIL: 'email',
    NONE: 'none'
} as const;

/**
 * Auth status types
 */
export const AUTH_STATUS = {
    SUCCESS: 'success',
    ERROR: 'error',
    FAILURE: 'failure',
    PENDING: 'pending'
} as const;

/**
 * KRA status types
 */
export const KRA_STATUS = {
    APPROVED: 'APPROVED',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED',
    NOT_SUBMITTED: 'NOT_SUBMITTED'
} as const;

/**
 * Product types
 */
export const PRODUCT_TYPES = {
    INTRADAY: 'INTRADAY',
    DELIVERY: 'DELIVERY',
    MIS: 'MIS',
    CNC: 'CNC'
} as const;

/**
 * Exchange types
 */
export const EXCHANGES = {
    NSE: 'NSE',
    BSE: 'BSE',
    NFO: 'NFO',
    BFO: 'BFO',
    MCX: 'MCX',
    NCDEX: 'NCDEX',
    CDS: 'CDS'
} as const;

// ==================== TYPE EXPORTS ====================

export type UserPreference = typeof USER_PREFERENCES[keyof typeof USER_PREFERENCES];
export type MfaType = typeof MFA_TYPES[keyof typeof MFA_TYPES];
export type AuthStatus = typeof AUTH_STATUS[keyof typeof AUTH_STATUS];
export type KraStatus = typeof KRA_STATUS[keyof typeof KRA_STATUS];
export type ProductType = typeof PRODUCT_TYPES[keyof typeof PRODUCT_TYPES];
export type ExchangeType = typeof EXCHANGES[keyof typeof EXCHANGES];

/**
 * Complete user session data
 */
export interface UserSession {
    user: LoginInfo;
    refreshData: RefreshData;
    sessionTime: number;
    isValid: boolean;
}

/**
 * Authentication context data
 */
export interface AuthContext {
    user: LoginInfo | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    login: (credentials: LoginPayload) => Promise<void>;
    logout: () => Promise<void>;
    refreshSession: () => Promise<void>;
}

// ==================== DEFAULT VALUES ====================

/**
 * Default LoginInfo object
 */
export const DEFAULT_LOGIN_INFO: Partial<LoginInfo> = {
    access_token: '',
    account_age_nudge: false,
    admin_reject_nudge: '',
    choice_of_nominee: '',
    deviceId: '',
    dp_client_id: '',
    dp_id: '',
    email: '',
    exchanges: [],
    isDematActive: false,
    is_maintainance: false,
    is_promoter_client: false,
    kra_status: KRA_STATUS.NOT_SUBMITTED,
    login_time: '',
    mfTradeActive: false,
    mfaType: MFA_TYPES.NONE,
    mfa_type: MFA_TYPES.NONE,
    mobile: '',
    oc_token: '',
    others: { userCode: '', POA: 0 },
    product_types: [],
    re_kyc_type: '',
    referral_id: '',
    ssoToken: '',
    user_id: '',
    user_name: '',
    user_preference: USER_PREFERENCES.INVESTOR,
    mpin_enabled: false,
    tradingActive: false,
    is_mpin_set: false,
};

/**
 * Default RefreshData object
 */
export const DEFAULT_REFRESH_DATA: RefreshData = {
    deviceId: '',
    mfaType: MFA_TYPES.NONE,
    ssoToken: '',
    tradingActive: false,
    refreshToken: '',
};